import { defineRule, configure } from "vee-validate";
import AllRules from "@vee-validate/rules";
import { localize, setLocale as setVeeLocale } from "@vee-validate/i18n";
import { setLocale as setYupLocale } from "yup";
import i18n from "@/core/plugins/i18n";
import enUS from "@vee-validate/i18n/dist/locale/en.json";
import zhCN from "@vee-validate/i18n/dist/locale/zh_CN.json";
import zhHK from "@vee-validate/i18n/dist/locale/zh_TW.json";
import zhTW from "@vee-validate/i18n/dist/locale/zh_TW.json";
import viVN from "@vee-validate/i18n/dist/locale/vi.json";
import thTH from "@vee-validate/i18n/dist/locale/th.json";
import jpJP from "@vee-validate/i18n/dist/locale/ja.json";
// import mnMN from "@vee-validate/i18n/dist/locale/mn.json";
import idID from "@vee-validate/i18n/dist/locale/id.json";
import msMY from "@vee-validate/i18n/dist/locale/ms_MY.json";
import koKR from "@vee-validate/i18n/dist/locale/ko.json";
import kmKH from "@vee-validate/i18n/dist/locale/km.json";
import esES from "@vee-validate/i18n/dist/locale/es.json";

const { t, te } = i18n.global;

const myRules = {
  minMax: (value, [min, max]) => {
    // The field is empty so it should pass
    if (!value || !value.length) {
      return true;
    }
    const numericValue = Number(value);
    if (numericValue < min) {
      return `This field must be greater than ${min}`;
    }
    if (numericValue > max) {
      return `This field must be less than ${max}`;
    }
    return true;
  },
  minLength: (value, [limit]) => {
    // The field is empty so it should pass
    if (!value || !value.length) {
      return true;
    }
    if (value.length < limit) {
      return `This field must be at least ${limit} characters`;
    }
    return true;
  },

  accountPassword: (value) => {
    const regex =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/;
    if (!regex.test(value)) {
      return t("tip.accountPasswordValidate");
    }
    return true;
  },
  ...AllRules,
};

type YupLocaleMessageSet = {
  default: string;
  required: string;
  oneOf: string;
  notOneOf: string;
  string: {
    length: string;
    min: string;
    max: string;
    matches: string;
    email: string;
    url: string;
    uuid: string;
    trim: string;
    lowercase: string;
    uppercase: string;
  };
  number: {
    min: string;
    max: string;
    lessThan: string;
    moreThan: string;
    positive: string;
    negative: string;
    integer: string;
  };
};

const yupLocaleMessages: Record<string, YupLocaleMessageSet> = {
  ["en-us"]: {
    default: "${path} is invalid",
    required: "${path} is a required field",
    oneOf: "${path} must be one of the following values: ${values}",
    notOneOf: "${path} must not be one of the following values: ${values}",
    string: {
      length: "${path} must be exactly ${length} characters",
      min: "${path} must be at least ${min} characters",
      max: "${path} must be at most ${max} characters",
      matches: '${path} must match the following: "${regex}"',
      email: "${path} must be a valid email",
      url: "${path} must be a valid URL",
      uuid: "${path} must be a valid UUID",
      trim: "${path} must be a trimmed string",
      lowercase: "${path} must be a lowercase string",
      uppercase: "${path} must be a upper case string",
    },
    number: {
      min: "${path} must be greater than or equal to ${min}",
      max: "${path} must be less than or equal to ${max}",
      lessThan: "${path} must be less than ${less}",
      moreThan: "${path} must be greater than ${more}",
      positive: "${path} must be a positive number",
      negative: "${path} must be a negative number",
      integer: "${path} must be an integer",
    },
  },
  ["zh-cn"]: {
    default: "${path} 无效",
    required: "${path} 为必填项",
    oneOf: "${path} 必须是以下值之一: ${values}",
    notOneOf: "${path} 不能是以下值之一: ${values}",
    string: {
      length: "${path} 必须正好为 ${length} 个字符",
      min: "${path} 至少为 ${min} 个字符",
      max: "${path} 至多为 ${max} 个字符",
      matches: '${path} 必须匹配以下规则: "${regex}"',
      email: "${path} 必须是有效的邮箱",
      url: "${path} 必须是有效的 URL",
      uuid: "${path} 必须是有效的 UUID",
      trim: "${path} 必须是去除首尾空格的字符串",
      lowercase: "${path} 必须是小写字符串",
      uppercase: "${path} 必须是大写字符串",
    },
    number: {
      min: "${path} 必须大于或等于 ${min}",
      max: "${path} 必须小于或等于 ${max}",
      lessThan: "${path} 必须小于 ${less}",
      moreThan: "${path} 必须大于 ${more}",
      positive: "${path} 必须为正数",
      negative: "${path} 必须为负数",
      integer: "${path} 必须为整数",
    },
  },
  ["zh-hk"]: {
    default: "${path} 無效",
    required: "${path} 為必填欄位",
    oneOf: "${path} 必須是以下值之一: ${values}",
    notOneOf: "${path} 不能是以下值之一: ${values}",
    string: {
      length: "${path} 必須正好為 ${length} 個字元",
      min: "${path} 至少為 ${min} 個字元",
      max: "${path} 至多為 ${max} 個字元",
      matches: '${path} 必須符合以下規則: "${regex}"',
      email: "${path} 必須為有效的電郵",
      url: "${path} 必須為有效的 URL",
      uuid: "${path} 必須為有效的 UUID",
      trim: "${path} 必須為去除首尾空白的字串",
      lowercase: "${path} 必須為小寫字串",
      uppercase: "${path} 必須為大寫字串",
    },
    number: {
      min: "${path} 必須大於或等於 ${min}",
      max: "${path} 必須小於或等於 ${max}",
      lessThan: "${path} 必須小於 ${less}",
      moreThan: "${path} 必須大於 ${more}",
      positive: "${path} 必須為正數",
      negative: "${path} 必須為負數",
      integer: "${path} 必須為整數",
    },
  },
  ["zh-tw"]: {
    default: "${path} 無效",
    required: "${path} 為必填欄位",
    oneOf: "${path} 必須是以下值之一: ${values}",
    notOneOf: "${path} 不能是以下值之一: ${values}",
    string: {
      length: "${path} 必須正好為 ${length} 個字元",
      min: "${path} 至少為 ${min} 個字元",
      max: "${path} 至多為 ${max} 個字元",
      matches: '${path} 必須符合以下規則: "${regex}"',
      email: "${path} 必須為有效的電子郵件",
      url: "${path} 必須為有效的 URL",
      uuid: "${path} 必須為有效的 UUID",
      trim: "${path} 必須為去除首尾空白的字串",
      lowercase: "${path} 必須為小寫字串",
      uppercase: "${path} 必須為大寫字串",
    },
    number: {
      min: "${path} 必須大於或等於 ${min}",
      max: "${path} 必須小於或等於 ${max}",
      lessThan: "${path} 必須小於 ${less}",
      moreThan: "${path} 必須大於 ${more}",
      positive: "${path} 必須為正數",
      negative: "${path} 必須為負數",
      integer: "${path} 必須為整數",
    },
  },
  ["vi-vn"]: {
    default: "${path} không hợp lệ",
    required: "${path} là trường bắt buộc",
    oneOf: "${path} phải là một trong các giá trị sau: ${values}",
    notOneOf: "${path} không được là một trong các giá trị sau: ${values}",
    string: {
      length: "${path} phải có đúng ${length} ký tự",
      min: "${path} phải có ít nhất ${min} ký tự",
      max: "${path} phải có nhiều nhất ${max} ký tự",
      matches: '${path} phải khớp với: "${regex}"',
      email: "${path} phải là email hợp lệ",
      url: "${path} phải là URL hợp lệ",
      uuid: "${path} phải là UUID hợp lệ",
      trim: "${path} phải là chuỗi đã được cắt khoảng trắng",
      lowercase: "${path} phải là chữ thường",
      uppercase: "${path} phải là chữ hoa",
    },
    number: {
      min: "${path} phải lớn hơn hoặc bằng ${min}",
      max: "${path} phải nhỏ hơn hoặc bằng ${max}",
      lessThan: "${path} phải nhỏ hơn ${less}",
      moreThan: "${path} phải lớn hơn ${more}",
      positive: "${path} phải là số dương",
      negative: "${path} phải là số âm",
      integer: "${path} phải là số nguyên",
    },
  },
  ["th-th"]: {
    default: "${path} ไม่ถูกต้อง",
    required: "${path} เป็นฟิลด์ที่จำเป็น",
    oneOf: "${path} ต้องเป็นหนึ่งในค่าต่อไปนี้: ${values}",
    notOneOf: "${path} ต้องไม่เป็นหนึ่งในค่าต่อไปนี้: ${values}",
    string: {
      length: "${path} ต้องยาวเท่ากับ ${length} ตัวอักษร",
      min: "${path} ต้องมีอย่างน้อย ${min} ตัวอักษร",
      max: "${path} ต้องมีไม่เกิน ${max} ตัวอักษร",
      matches: '${path} ต้องตรงกับรูปแบบ: "${regex}"',
      email: "${path} ต้องเป็นอีเมลที่ถูกต้อง",
      url: "${path} ต้องเป็น URL ที่ถูกต้อง",
      uuid: "${path} ต้องเป็น UUID ที่ถูกต้อง",
      trim: "${path} ต้องเป็นสตริงที่ตัดช่องว่างแล้ว",
      lowercase: "${path} ต้องเป็นตัวพิมพ์เล็ก",
      uppercase: "${path} ต้องเป็นตัวพิมพ์ใหญ่",
    },
    number: {
      min: "${path} ต้องมากกว่าหรือเท่ากับ ${min}",
      max: "${path} ต้องน้อยกว่าหรือเท่ากับ ${max}",
      lessThan: "${path} ต้องน้อยกว่า ${less}",
      moreThan: "${path} ต้องมากกว่า ${more}",
      positive: "${path} ต้องเป็นจำนวนบวก",
      negative: "${path} ต้องเป็นจำนวนลบ",
      integer: "${path} ต้องเป็นจำนวนเต็ม",
    },
  },
  ["jp-jp"]: {
    default: "${path} は無効です",
    required: "${path} は必須項目です",
    oneOf: "${path} は次のいずれかの値である必要があります: ${values}",
    notOneOf: "${path} は次のいずれかの値であってはなりません: ${values}",
    string: {
      length: "${path} はちょうど ${length} 文字である必要があります",
      min: "${path} は少なくとも ${min} 文字である必要があります",
      max: "${path} は最大 ${max} 文字である必要があります",
      matches: '${path} は次の正規表現に一致する必要があります: "${regex}"',
      email: "${path} は有効なメールアドレスである必要があります",
      url: "${path} は有効なURLである必要があります",
      uuid: "${path} は有効なUUIDである必要があります",
      trim: "${path} は前後の空白を取り除いた文字列である必要があります",
      lowercase: "${path} は小文字である必要があります",
      uppercase: "${path} は大文字である必要があります",
    },
    number: {
      min: "${path} は ${min} 以上である必要があります",
      max: "${path} は ${max} 以下である必要があります",
      lessThan: "${path} は ${less} 未満である必要があります",
      moreThan: "${path} は ${more} より大きい必要があります",
      positive: "${path} は正の数である必要があります",
      negative: "${path} は負の数である必要があります",
      integer: "${path} は整数である必要があります",
    },
  },
  ["id-id"]: {
    default: "${path} tidak valid",
    required: "${path} wajib diisi",
    oneOf: "${path} harus salah satu dari nilai berikut: ${values}",
    notOneOf: "${path} tidak boleh salah satu dari nilai berikut: ${values}",
    string: {
      length: "${path} harus tepat ${length} karakter",
      min: "${path} minimal ${min} karakter",
      max: "${path} maksimal ${max} karakter",
      matches: '${path} harus cocok dengan: "${regex}"',
      email: "${path} harus berupa email yang valid",
      url: "${path} harus berupa URL yang valid",
      uuid: "${path} harus berupa UUID yang valid",
      trim: "${path} harus berupa string yang sudah di-trim",
      lowercase: "${path} harus berupa huruf kecil",
      uppercase: "${path} harus berupa huruf besar",
    },
    number: {
      min: "${path} harus lebih besar atau sama dengan ${min}",
      max: "${path} harus lebih kecil atau sama dengan ${max}",
      lessThan: "${path} harus kurang dari ${less}",
      moreThan: "${path} harus lebih besar dari ${more}",
      positive: "${path} harus berupa angka positif",
      negative: "${path} harus berupa angka negatif",
      integer: "${path} harus berupa bilangan bulat",
    },
  },
  ["ms-my"]: {
    default: "${path} tidak sah",
    required: "${path} ialah medan yang diperlukan",
    oneOf: "${path} mesti salah satu daripada nilai berikut: ${values}",
    notOneOf:
      "${path} tidak boleh salah satu daripada nilai berikut: ${values}",
    string: {
      length: "${path} mesti tepat ${length} aksara",
      min: "${path} mesti sekurang-kurangnya ${min} aksara",
      max: "${path} mesti tidak melebihi ${max} aksara",
      matches: '${path} mesti sepadan dengan: "${regex}"',
      email: "${path} mesti emel yang sah",
      url: "${path} mesti URL yang sah",
      uuid: "${path} mesti UUID yang sah",
      trim: "${path} mesti rentetan yang telah dipangkas",
      lowercase: "${path} mesti huruf kecil",
      uppercase: "${path} mesti huruf besar",
    },
    number: {
      min: "${path} mesti lebih besar atau sama dengan ${min}",
      max: "${path} mesti kurang atau sama dengan ${max}",
      lessThan: "${path} mesti kurang daripada ${less}",
      moreThan: "${path} mesti lebih besar daripada ${more}",
      positive: "${path} mesti nombor positif",
      negative: "${path} mesti nombor negatif",
      integer: "${path} mesti nombor bulat",
    },
  },
  ["ko-kr"]: {
    default: "${path} 유효하지 않습니다",
    required: "${path} 은(는) 필수 항목입니다",
    oneOf: "${path} 은(는) 다음 값 중 하나여야 합니다: ${values}",
    notOneOf: "${path} 은(는) 다음 값 중 하나일 수 없습니다: ${values}",
    string: {
      length: "${path} 은(는) 정확히 ${length}자여야 합니다",
      min: "${path} 은(는) 최소 ${min}자여야 합니다",
      max: "${path} 은(는) 최대 ${max}자여야 합니다",
      matches: '${path} 은(는) 다음과 일치해야 합니다: "${regex}"',
      email: "${path} 은(는) 올바른 이메일이어야 합니다",
      url: "${path} 은(는) 올바른 URL이어야 합니다",
      uuid: "${path} 은(는) 올바른 UUID여야 합니다",
      trim: "${path} 은(는) 공백이 제거된 문자열이어야 합니다",
      lowercase: "${path} 은(는) 소문자여야 합니다",
      uppercase: "${path} 은(는) 대문자여야 합니다",
    },
    number: {
      min: "${path} 은(는) ${min} 이상이어야 합니다",
      max: "${path} 은(는) ${max} 이하이어야 합니다",
      lessThan: "${path} 은(는) ${less}보다 작아야 합니다",
      moreThan: "${path} 은(는) ${more}보다 커야 합니다",
      positive: "${path} 은(는) 양수여야 합니다",
      negative: "${path} 은(는) 음수여야 합니다",
      integer: "${path} 은(는) 정수여야 합니다",
    },
  },
  ["km-kh"]: {
    default: "${path} មិនត្រឹមត្រូវ",
    required: "${path} ជាវាលដែលត្រូវបំពេញ",
    oneOf: "${path} ត្រូវតែជាមួយក្នុងតម្លៃដូចតទៅ: ${values}",
    notOneOf: "${path} មិនត្រូវជាមួយក្នុងតម្លៃដូចតទៅ: ${values}",
    string: {
      length: "${path} ត្រូវមាន ${length} តួអក្សរត្រឹមត្រូវ",
      min: "${path} ត្រូវមានយ៉ាងតិច ${min} តួអក្សរ",
      max: "${path} ត្រូវមានយ៉ាងច្រើន ${max} តួអក្សរ",
      matches: '${path} ត្រូវតែផ្គូផ្គង: "${regex}"',
      email: "${path} ត្រូវតែជាអ៊ីមែលត្រឹមត្រូវ",
      url: "${path} ត្រូវតែជាអាសយដ្ឋាន URL ត្រឹមត្រូវ",
      uuid: "${path} ត្រូវតែជា UUID ត្រឹមត្រូវ",
      trim: "${path} ត្រូវតែជាខ្សែអក្សរដែលបានកាត់ចន្លោះ",
      lowercase: "${path} ត្រូវតែជាអក្សរតូច",
      uppercase: "${path} ត្រូវតែជាអក្សរធំ",
    },
    number: {
      min: "${path} ត្រូវតែធំជាងឬស្មើ ${min}",
      max: "${path} ត្រូវតែតូចជាងឬស្មើ ${max}",
      lessThan: "${path} ត្រូវតែតូចជាង ${less}",
      moreThan: "${path} ត្រូវតែធំជាង ${more}",
      positive: "${path} ត្រូវតែជាចំនួនវិជ្ជមាន",
      negative: "${path} ត្រូវតែជាចំនួនអវិជ្ជមាន",
      integer: "${path} ត្រូវតែជាចំនួនគត់",
    },
  },
  ["es-es"]: {
    default: "${path} no es válido",
    required: "${path} es un campo obligatorio",
    oneOf: "${path} debe ser uno de los siguientes valores: ${values}",
    notOneOf: "${path} no debe ser uno de los siguientes valores: ${values}",
    string: {
      length: "${path} debe tener exactamente ${length} caracteres",
      min: "${path} debe tener al menos ${min} caracteres",
      max: "${path} debe tener como máximo ${max} caracteres",
      matches: '${path} debe coincidir con: "${regex}"',
      email: "${path} debe ser un correo electrónico válido",
      url: "${path} debe ser una URL válida",
      uuid: "${path} debe ser un UUID válido",
      trim: "${path} debe ser una cadena recortada",
      lowercase: "${path} debe estar en minúsculas",
      uppercase: "${path} debe estar en mayúsculas",
    },
    number: {
      min: "${path} debe ser mayor o igual que ${min}",
      max: "${path} debe ser menor o igual que ${max}",
      lessThan: "${path} debe ser menor que ${less}",
      moreThan: "${path} debe ser mayor que ${more}",
      positive: "${path} debe ser un número positivo",
      negative: "${path} debe ser un número negativo",
      integer: "${path} debe ser un número entero",
    },
  },
};

const translatePath = (path: string) => {
  if (!path) return path;
  if (te(path)) return t(path);

  const normalized = path.trim();
  const lower = normalized.toLowerCase();
  const noSpace = lower.replace(/\s+/g, "");
  const snake = lower.replace(/\s+/g, "_");

  const candidates = [
    `fields.${normalized}`,
    `fields.${lower}`,
    `fields.${noSpace}`,
    `fields.${snake}`,
    lower,
  ];

  for (const key of candidates) {
    if (te(key)) return t(key);
  }

  return path;
};

const formatYupMessage = (template: string, params: Record<string, any>) =>
  template.replace(/\$\{(\w+)\}/g, (_, key) => {
    if (key === "path") {
      return translatePath(
        params.path || params.label || params.originalPath || ""
      );
    }
    const value = params[key];
    return value === undefined || value === null ? "" : String(value);
  });

const applyYupLocale = (localeCode: string) => {
  const messages = yupLocaleMessages[localeCode] || yupLocaleMessages["en-us"];
  setYupLocale({
    mixed: {
      default: (params) => formatYupMessage(messages.default, params),
      required: (params) => formatYupMessage(messages.required, params),
      oneOf: (params) => formatYupMessage(messages.oneOf, params),
      notOneOf: (params) => formatYupMessage(messages.notOneOf, params),
    },
    string: {
      length: (params) => formatYupMessage(messages.string.length, params),
      min: (params) => formatYupMessage(messages.string.min, params),
      max: (params) => formatYupMessage(messages.string.max, params),
      matches: (params) => formatYupMessage(messages.string.matches, params),
      email: (params) => formatYupMessage(messages.string.email, params),
      url: (params) => formatYupMessage(messages.string.url, params),
      uuid: (params) => formatYupMessage(messages.string.uuid, params),
      trim: (params) => formatYupMessage(messages.string.trim, params),
      lowercase: (params) =>
        formatYupMessage(messages.string.lowercase, params),
      uppercase: (params) =>
        formatYupMessage(messages.string.uppercase, params),
    },
    number: {
      min: (params) => formatYupMessage(messages.number.min, params),
      max: (params) => formatYupMessage(messages.number.max, params),
      lessThan: (params) => formatYupMessage(messages.number.lessThan, params),
      moreThan: (params) => formatYupMessage(messages.number.moreThan, params),
      positive: (params) => formatYupMessage(messages.number.positive, params),
      negative: (params) => formatYupMessage(messages.number.negative, params),
      integer: (params) => formatYupMessage(messages.number.integer, params),
    },
  });
};

const initGlobalValidators = () => {
  Object.keys(myRules).forEach((rule) => {
    defineRule(rule, myRules[rule]);
  });
  const currentLocale = (
    localStorage.getItem("language") ||
    i18n.global.locale.value ||
    "en-us"
  )
    .toString()
    .toLowerCase();
  setVeeLocale(currentLocale);
  applyYupLocale(currentLocale);
};

const withOverrides = (locale, overrides) => ({
  ...locale,
  messages: {
    ...locale.messages,
    ...overrides,
  },
});

configure({
  generateMessage: localize({
    ["en-us"]: withOverrides(enUS, {
      _default: "{field} is invalid",
      required: "{field} is a required field",
      one_of: "{field} must be one of the following values: {values}",
      not_one_of: "{field} must not be one of the following values: {values}",
    }),
    ["zh-cn"]: withOverrides(zhCN, {
      _default: "{field} 无效",
      required: "{field} 为必填项",
      one_of: "{field} 必须是以下值之一: {values}",
      not_one_of: "{field} 不能是以下值之一: {values}",
    }),
    ["zh-hk"]: withOverrides(zhHK, {
      _default: "{field} 無效",
      required: "{field} 為必填欄位",
      one_of: "{field} 必須是以下值之一: {values}",
      not_one_of: "{field} 不能是以下值之一: {values}",
    }),
    ["zh-tw"]: withOverrides(zhTW, {
      _default: "{field} 無效",
      required: "{field} 為必填欄位",
      one_of: "{field} 必須是以下值之一: {values}",
      not_one_of: "{field} 不能是以下值之一: {values}",
    }),
    ["vi-vn"]: withOverrides(viVN, {
      _default: "{field} không hợp lệ",
      required: "{field} là trường bắt buộc",
      one_of: "{field} phải là một trong các giá trị sau: {values}",
      not_one_of: "{field} không được là một trong các giá trị sau: {values}",
    }),
    ["th-th"]: withOverrides(thTH, {
      _default: "{field} ไม่ถูกต้อง",
      required: "{field} เป็นฟิลด์ที่จำเป็น",
      one_of: "{field} ต้องเป็นหนึ่งในค่าต่อไปนี้: {values}",
      not_one_of: "{field} ต้องไม่เป็นหนึ่งในค่าต่อไปนี้: {values}",
    }),
    ["jp-jp"]: withOverrides(jpJP, {
      _default: "{field} は無効です",
      required: "{field} は必須項目です",
      one_of: "{field} は次のいずれかの値である必要があります: {values}",
      not_one_of: "{field} は次のいずれかの値であってはなりません: {values}",
    }),
    // ["mn-mn"]: withOverrides(mnMN, {
    //   _default: "{field} is invalid",
    //   required: "{field} is a required field",
    //   one_of: "{field} must be one of the following values: {values}",
    //   not_one_of: "{field} must not be one of the following values: {values}",
    // }),
    ["id-id"]: withOverrides(idID, {
      _default: "{field} tidak valid",
      required: "{field} wajib diisi",
      one_of: "{field} harus salah satu dari nilai berikut: {values}",
      not_one_of: "{field} tidak boleh salah satu dari nilai berikut: {values}",
    }),
    ["ms-my"]: withOverrides(msMY, {
      _default: "{field} tidak sah",
      required: "{field} ialah medan yang diperlukan",
      one_of: "{field} mesti salah satu daripada nilai berikut: {values}",
      not_one_of:
        "{field} tidak boleh salah satu daripada nilai berikut: {values}",
    }),
    ["ko-kr"]: withOverrides(koKR, {
      _default: "{field} 유효하지 않습니다",
      required: "{field} 은(는) 필수 항목입니다",
      one_of: "{field} 은(는) 다음 값 중 하나여야 합니다: {values}",
      not_one_of: "{field} 은(는) 다음 값 중 하나일 수 없습니다: {values}",
    }),
    ["km-kh"]: withOverrides(kmKH, {
      _default: "{field} មិនត្រឹមត្រូវ",
      required: "{field} ជាវាលដែលត្រូវបំពេញ",
      one_of: "{field} ត្រូវតែជាមួយក្នុងតម្លៃដូចតទៅ: {values}",
      not_one_of: "{field} មិនត្រូវជាមួយក្នុងតម្លៃដូចតទៅ: {values}",
    }),
    ["es-es"]: withOverrides(esES, {
      _default: "{field} no es válido",
      required: "{field} es un campo obligatorio",
      one_of: "{field} debe ser uno de los siguientes valores: {values}",
      not_one_of: "{field} no debe ser uno de los siguientes valores: {values}",
    }),
  }),
});

export default initGlobalValidators;
